import { observable, action, computed, reaction } from 'mobx';
import i18next from 'i18next';
import Alert from 'react-s-alert';

import { AppManager, Input } from 'core';
import { analytics } from 'core/analytics';

import { WizardStore } from 'libs/wizards/app';
import { clientStore } from 'apps/client/app';

import { SpaceApp } from 'apps/space/app';
import { ProfilesApp } from 'apps/profile/app';

import { redirectToContent, raiseUserError } from 'apps/utils';
import { importServices } from 'libs/forms/validators';
import { conf } from 'apps/project/config.yaml';
import { modalStore } from 'apps/modals/app';

import { ImportProjectMutation } from 'apps/project/gql';

import { isTrialExpired } from 'utils/freeTrials';
import { FreeTrialMessage } from 'utils/freeTrialMessage';

import { ModalFreeTrialInfo } from 'apps/modals/freeTrial/info';

export class ImportProjectAppManager extends AppManager {
  static registryName = 'ImportProjectAppManager';
  @observable importURL = '';
  @observable inSpace = '';
  @observable name = '';
  @observable projectType = 'private';
  @observable hasSubscribed = false;

  constructor(parent, init = {}) {
    super(parent, init);
    this.AppRef('inSpace', SpaceApp);
    this.AppRef('invites', ProfilesApp);

    // mutates conf
    this.patchTrialMessage(conf);

    conf.importinfo.formTitle = conf.importinfo.formTitle.replace(
      '{services}',
      importServices.join(', '),
    );

    this.wizard = new WizardStore(this, conf, 'projectImportWizard', {
      BeginImport: this.BeginImport,
    });

    this.importProjectInput = new Input({
      space: () => this.inSpace.path,
      name: () => this.name,
      private: () => !!this.projectType && this.projectType === 'private',
      importUrl: () => this.importURL,
    });

    this.disposers.push(
      reaction(
        () => ({
          spaceId: this.inSpace?.id || '',
          projectType: this.projectType,
        }),
        () => {
          const form = this.wizard.formStores.get('importinfo');

          form.SetButtonDisabled(this.requiresPayment);
        },
      ),
    );

    this.disposers.push(
      reaction(
        () => ({
          workspaces: clientStore.workspaces,
          activeSpaceId: clientStore.activeSpaceId,
          optionsManager: this.wizard.formStores
            .get('importinfo')
            .fieldStores.get('inSpace').optionsManager,
        }),
        ({ workspaces, activeSpaceId, optionsManager }) => {
          if (!optionsManager) return;
          this.optionsOverride = workspaces;
          // eslint-disable-next-line no-param-reassign
          optionsManager.options = this.optionsOverride;

          const activeSpace = optionsManager.options.find(
            option => option.id === activeSpaceId,
          );

          if (activeSpace) this.inSpace = activeSpace;
        },
      ),
    );
  }

  @action patchTrialMessage = formConf => {
    const privateFieldConf =
      formConf?.fields?.projectType?.options?.values?.[0];

    if (!privateFieldConf) return;
    privateFieldConf.extraMessage = {
      component: FreeTrialMessage,
      props: { appManager: this },
      showOnSelect: true,
    };
  };

  @computed get requiresPayment() {
    if (!this.inSpace || this.hasSubscribed) return false;
    const {
      hasPrivateProjectsSubscription,
      hasBillingPermissions,
      trialPeriodEnd,
    } = this.inSpace;

    return (
      !!this.importProjectInput.private &&
      !hasPrivateProjectsSubscription &&
      hasBillingPermissions &&
      isTrialExpired(trialPeriodEnd)
    );
  }

  @action BeginImport = () => {
    if (this.wizard.activeForm.isValid) {
      this.wizard.activeForm.SetPosting(true);
      clientStore.SetPosting(true);
      const { onSysErr, onUserErr, onSuccess } = this;
      const options = { onSysErr, onUserErr, onSuccess };

      // Call the import project mutation.
      // Internally, that mutation will create the project

      this.mutations.push(
        ImportProjectMutation.New(this.importProjectInput, options),
      );
    } else {
      this.wizard.activeForm.alert();
    }
  };

  @action onSysErr = () => {
    this.wizard.activeForm.SetPosting(false);
    clientStore.SetPosting(false);
    Alert.error(
      i18next.t(
        'Hmm... We failed to create the project inside Wikifactory. Please try again.',
      ),
    );
  };

  @action onUserErr = error => {
    this.wizard.activeForm.SetPosting(false);
    clientStore.SetPosting(false);
    raiseUserError(error, 'import', 'Project');
  };

  @action onSuccess = result => {
    const {
      hasBillingPermissions,
      hasPrivateProjectsSubscription,
      trialPeriodEnd,
    } = this.inSpace;

    if (
      this.projectType &&
      hasBillingPermissions &&
      !hasPrivateProjectsSubscription &&
      !trialPeriodEnd
    ) {
      analytics.sendEvent(
        'start-free-trial',
        { space: this.inSpace.path },
        { ga: true, intercom: true },
      );
      analytics.sendEvent(
        'StartTrial',
        { value: 7, currency: 'EUR' },
        { twitter: true, facebook: true },
      );
    }
    analytics.sendEvent(
      'Schedule',
      { value: 8, currency: 'EUR' },
      { facebook: true },
    );
    this.wizard.activeForm.SetPosting(false);
    this.wizard.clearDraft();
    clientStore.SetPosting(false);

    // Redirect to the project page, where the user will be able to see the import process
    redirectToContent(result, 'project');

    Alert.success(i18next.t('The import process has started'));
  };

  showTrialModal = () => {
    const { spaceType } = this.inSpace;
    modalStore.SetModalComponent(ModalFreeTrialInfo, { spaceType });
  };
}
